import { type LocalDatabaseCustomerGroup } from "#/Data/_Bases/BaseCustomerGroupRepository.ts";

export interface CustomerGroup {
    id?: string;
    name: string;
    defaultReceipt: null | string;
    autoPrintReceipt: boolean;
    autoEmailReceipt: boolean;
    loyaltyEnabled: boolean;
    loyaltyRate: number | null;
    disablePromotions: boolean;
    priceList: null | string;
    allowAccountSales: boolean;
    accountLimit: number | null;
    paymentReceipt: null | string;
    emailReceipt: null | string;
    statementTemplate: null | string;
    autoPrintPaymentReceipt: boolean;
    autoPrintParkedReceipt: boolean;
    requireOrderReference: boolean;
    creditTerms: Array<string>;
}

export interface CustomerGroupFragment {
    id: string;
    name: string;
    defaultReceipt: null | {
        id: string;
    };
    autoPrintReceipt: boolean;
    autoEmailReceipt: boolean;
    loyaltyEnabled: boolean;
    loyaltyRate: number | null;
    disablePromotions: boolean;
    priceList: null | {
        id: string;
    };
    allowAccountSales: boolean;
    accountLimit: number | null;
    paymentReceipt: null | {
        id: string;
    };
    emailReceipt: null | {
        id: string;
    };
    statementTemplate: null | {
        id: string;
    };
    autoPrintPaymentReceipt: boolean;
    autoPrintParkedReceipt: boolean;
    requireOrderReference: boolean;
    creditTerms: Array<string>;
}

export const customerGroupFragment = `
    id,
    name,
    defaultReceipt {
        id,
    },
    autoPrintReceipt,
    autoEmailReceipt,
    loyaltyEnabled,
    loyaltyRate,
    disablePromotions,
    priceList {
        id,
    },
    allowAccountSales,
    accountLimit,
    emailReceipt {
        id,
    },
    paymentReceipt {
        id,
    },
    statementTemplate {
        id,
    },
    autoPrintPaymentReceipt,
    autoPrintParkedReceipt,
    creditTerms,
    requireOrderReference,
`;

export function normaliseCustomerGroup(group: CustomerGroupFragment): CustomerGroup {
    return {
        ...group,
        defaultReceipt   : group.defaultReceipt?.id ?? null,
        priceList        : group.priceList?.id ?? null,
        paymentReceipt   : group.paymentReceipt?.id ?? null,
        emailReceipt     : group.emailReceipt?.id ?? null,
        statementTemplate: group.statementTemplate?.id ?? null,
    };
}

export function transformCustomerGroupToIndexedDB(customerGroup: CustomerGroupFragment): LocalDatabaseCustomerGroup {
    return {
        uuid                     : customerGroup.id ?? "",
        name                     : customerGroup.name,
        account_limit            : customerGroup.accountLimit,
        account_receipt_id       : customerGroup.paymentReceipt ? customerGroup.paymentReceipt.id : null,
        allow_account_sales      : customerGroup.allowAccountSales,
        auto_email_receipt       : customerGroup.autoEmailReceipt,
        autoprint_account_receipt: customerGroup.autoPrintPaymentReceipt,
        autoprint_receipt        : customerGroup.autoPrintReceipt,
        autoprint_parked_receipt : customerGroup.autoPrintParkedReceipt,
        created_at               : "",
        credit_terms             : customerGroup.creditTerms,
        deleted_at               : null,
        disable_promotions       : customerGroup.disablePromotions,
        email_receipt_id         : customerGroup.emailReceipt ? customerGroup.emailReceipt.id : null,
        statement_template_id    : customerGroup.statementTemplate ? customerGroup.statementTemplate.id : null,
        loyalty                  : customerGroup.loyaltyEnabled,
        loyalty_rate             : customerGroup.loyaltyRate,
        pricelist_id             : customerGroup.priceList ? customerGroup.priceList.id : null,
        receipt_id               : customerGroup.defaultReceipt ? customerGroup.defaultReceipt.id : null,
        require_order_reference  : customerGroup.requireOrderReference,
        updated_at               : "",
    };
}

export function convertCustomerGroupIndexedDBToGraphQL(customerGroup: LocalDatabaseCustomerGroup): CustomerGroup {
    return {
        id                     : customerGroup.uuid,
        name                   : customerGroup.name,
        defaultReceipt         : customerGroup.receipt_id,
        autoPrintReceipt       : customerGroup.autoprint_receipt,
        autoEmailReceipt       : customerGroup.auto_email_receipt,
        loyaltyEnabled         : customerGroup.loyalty,
        loyaltyRate            : customerGroup.loyalty_rate,
        disablePromotions      : customerGroup.disable_promotions,
        priceList              : customerGroup.pricelist_id,
        allowAccountSales      : customerGroup.allow_account_sales,
        accountLimit           : customerGroup.account_limit,
        paymentReceipt         : customerGroup.account_receipt_id,
        emailReceipt           : customerGroup.email_receipt_id,
        statementTemplate      : customerGroup.statement_template_id,
        autoPrintPaymentReceipt: customerGroup.autoprint_account_receipt,
        autoPrintParkedReceipt : customerGroup.autoprint_parked_receipt,
        requireOrderReference  : customerGroup.require_order_reference,
        creditTerms            : customerGroup.credit_terms ?? [],
    };
}
